import React, { useContext } from "react";
import { Grid, CardContent, CardHeader, Card, Divider, Typography } from "@material-ui/core";
import OrdersTable from "./OrdersTable";
import { usePageStyles } from "../../pageStyles";
import { PageComponent } from "../../../core/navigation/types";
import { PageTitle } from "../../components/common/PageTitle";
import { useMainBoardData } from "../../../data/trading/mainBoard/hooks/useMainBoardData";
import { useMainBoardIndex } from "../../../data/trading/mainBoard/hooks/useMainBoardIndex";
import { OrderSide, PageDataStatus } from "../../../data/models";
import { MainBoarFilterButton } from "./MainBoardFilter";
import { useNotificationHandler } from "../../../core/notifications/hooks";
import { ProductGraph } from "./ProductGraph";
import { PriceTicker } from "./Ticker";
import { LocalizationContext } from "../../../core/localization/Localization";
import Button from "@material-ui/core/Button";
import { Rnd } from "react-rnd";
import { OrderIndFloatingView } from "./OrderIndFloatingView";
import ViewListIcon from "@material-ui/icons/ListAlt";
import { CustomTooltip } from "../../components/common/CustomTooltip";
import { useAuth } from "../../../data/auth/hooks";
import { AccessRights } from "../../../core/navigation/types";

const MainBoard: PageComponent = ({ userAccessRights }) => {
	const pageStyles = usePageStyles();
	const { getString } = useContext(LocalizationContext);

	const [buyPageData, dispatchLoadBuyOrders, invalidateData] = useMainBoardData(OrderSide.Buy);
	const [buyPageIndex, dispatchSetBuyIndex] = useMainBoardIndex(OrderSide.Buy);



	React.useEffect(() => {
		if (buyPageData && buyPageData.status === PageDataStatus.DataInvalidated) {
			dispatchLoadBuyOrders();
		}
	}, [dispatchLoadBuyOrders, buyPageData]);

	const [sellPageData, dispatchLoadSellOrders] = useMainBoardData(OrderSide.Sell);
	const [sellPageIndex, dispatchSetSellIndex] = useMainBoardIndex(OrderSide.Sell);

	React.useEffect(() => {
		if (sellPageData && sellPageData.status === PageDataStatus.DataInvalidated) {
			dispatchLoadSellOrders();
		}
	}, [dispatchLoadSellOrders, sellPageData]);

	React.useEffect(
		() => () => {
			invalidateData();
		},
		[]
	);

	useNotificationHandler<string>(
		"OrderChanged",
		e => {
			console.log("OrderChanged", e);
			invalidateData();
		},
		[]
	);

	const [auth] = useAuth();

	const [showOrderInd, setShowOrderInd] = React.useState(false);

	React.useEffect(() => {
		if (
			auth.status === "successful" &&
			(auth.user.roleCode === "MEMTRADER" ||
				auth.user.roleCode === "MEMMANAGER")
		) {
			setShowOrderInd(true);
		}
	}, [
		auth.status,
		auth.status === "successful" ? auth.user.roleCode : undefined
	]);

	// ubačen deo da čuva poziciju prozora za indikativne naloge
	const STORAGE_KEY = "orderIndWindow";

	const getInitialOrderIndWindowState = () => {
		try {
			const saved = localStorage.getItem(STORAGE_KEY);

			if (saved) {
				return JSON.parse(saved);
			}
		} catch {
			localStorage.removeItem(STORAGE_KEY);
		}

		return {
			x: 0,
			y: 0,
			width: window.innerWidth - 10,
			height: window.innerHeight - 10,
		};
	};

	const [windowState, setWindowState] = React.useState(getInitialOrderIndWindowState);

	if (auth.status !== "successful") {
		return null;
	}

	return (
		<Grid container className={pageStyles.rootSpacing} spacing={2}>
			<Grid item xs={12}>
				<PriceTicker />
			</Grid>
			<Grid item xs={12}>
				<Card>
					<CardHeader
						title={<PageTitle title="navMainTable" />}
						action={
							<>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 1
									}} 		>
									<CustomTooltip titleKeyword="navOrderIndicative">
										<Button onClick={() => setShowOrderInd(prev => !prev)}>
											<ViewListIcon style={{ color: "#f57c00" }} />
										</Button>
									</CustomTooltip>
									<MainBoarFilterButton />

								</div>

							</>
						} />
					<Divider />
					<CardContent>
						<Grid container direction="row" spacing={2}>
							<Grid item xs={12} md={6}>
								<Grid item>
									<Typography variant="h6">{getString("mainTableBid")}</Typography>
								</Grid>
								<OrdersTable
									pageIndex={buyPageIndex}
									pageData={buyPageData}
									onPageIndexChange={dispatchSetBuyIndex}
									userAccessRights={userAccessRights}
									getData={dispatchLoadBuyOrders}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Grid item>
									<Typography variant="h6">{getString("mainTableAsk")}</Typography>
								</Grid>
								<OrdersTable
									pageIndex={sellPageIndex}
									pageData={sellPageData}
									onPageIndexChange={dispatchSetSellIndex}
									userAccessRights={userAccessRights}
									getData={dispatchLoadSellOrders}
								/>
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			</Grid>
			<Grid item md={4} xs={12}>
				<ProductGraph graphId="0001" />
			</Grid>
			<Grid item md={4} xs={12}>
				<ProductGraph graphId="0002" />
			</Grid>
			<Grid item md={4} xs={12}>
				<ProductGraph graphId="0003" />
			</Grid>
			{showOrderInd && (
				<Rnd
					size={{
						width: windowState.width,
						height: windowState.height,
					}}
					position={{
						x: windowState.x,
						y: windowState.y,
					}}
					minWidth={300}
					minHeight={200}
					bounds="window"
					dragHandleClassName="handle"
					style={{
						zIndex: 1200,
					}}
					onDragStop={(e, d) => {
						const newState = {
							...windowState,
							x: d.x,
							y: d.y,
						};

						setWindowState(newState);
						localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
					}}
					onResizeStop={(e, direction, ref, delta, position) => {
						const newState = {
							x: position.x,
							y: position.y,
							width: ref.offsetWidth,
							height: ref.offsetHeight,
						};

						setWindowState(newState);
						localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
					}}
				>
					<div
						style={{
							height: "100%",
							display: "flex",
							flexDirection: "column",
							background: "white",
							border: "1px solid #ccc",
							boxShadow: "0 2px 10px rgba(0,0,0,0.2)"
						}}
					>
						{/* HEADER */}
						<div
							className="handle"
							style={{
								cursor: "move",
								background: "#eee",
								padding: 8,
								fontWeight: "bold"
							}}
						>
							{getString("navOrderIndicative")}

							<span
								style={{ float: "right", cursor: "pointer" }}
								onClick={() => setShowOrderInd(false)}
							>
								✖
							</span>
						</div>

						<OrderIndFloatingView onClose={() => setShowOrderInd(false)} />
					</div>
				</Rnd>
			)}

		</Grid>
	);
};

export default MainBoard;
